import { useState, useEffect, useMemo, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2, Minus, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@shared/utils/cn';
import { useTenant } from '@core/tenant';
import { MAX_PARTY_SIZE } from '@core/config/constants';
import { ApiError } from '@core/api/apiClient';
import { captureApiError, capturePaymentError } from '@core/sentry';
import { Checkbox } from '@shared/components/ui/checkbox';
import GuaranteeChangeNotice from '@features/booking/components/GuaranteeChangeNotice';
import MercadoPagoCardFields, { type MercadoPagoCardHandle } from '@features/booking/components/payment/MercadoPagoCardFields';
import { CardInputError } from '@features/booking/utils/cardInput';
import { usePartySizeQuote } from '@features/booking/hooks/usePartySizeQuote';
import {
  chargeNow,
  GUARANTEE_REFUSAL,
  GUARANTEE_REQUIRES,
  guaranteeRefusal,
  isPaymentRefusal,
  type GuaranteeAdjustment,
} from '@features/booking/api/guaranteeAdjustment';
import type { Reservation } from '../hooks/useUserReservations';
import { userReservationsKeys } from '../hooks/useUserReservations';
import {
  checkModifyTimePolicy,
  fetchAvailableSizesForEdition,
  fetchPartySizeQuote,
  submitSizeChange,
} from '../api/reservationApi';
import type { AcceptedGuarantee, PartySizeInfo } from '../api/reservationApi';

type Step = 'loading' | 'choose' | 'confirm';

/** The no-show conditions the guest accepts with a new amount. */
type Acceptance = 'honorReservation' | 'cancelReservation' | 'termsAndConditions';
const NOTHING_ACCEPTED: ReadonlySet<Acceptance> = new Set();

interface ChangeSizeViewProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangeSizeView = ({ reservation, onClose, onSuccess }: ChangeSizeViewProps) => {
  const { t } = useTranslation('profile');
  const { t: tBooking } = useTranslation('booking');
  const { subdomainPrefix, restaurant } = useTenant();
  const queryClient = useQueryClient();

  const minSize = typeof restaurant?.minPartySizeAllowed === 'number'
    ? restaurant.minPartySizeAllowed
    : 1;

  const [step, setStep] = useState<Step>('loading');
  const [sizeInfo, setSizeInfo] = useState<PartySizeInfo[]>([]);
  const [selectedSize, setSelectedSize] = useState(reservation.partySize);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancellationHours, setCancellationHours] = useState(0);
  // A size whose acceptance the backend asked for but this app could not show: the guest contacts the restaurant.
  const [unacceptableSize, setUnacceptableSize] = useState<number | null>(null);
  // Set once a charging POST answers with an unknown outcome (5xx/network/timeout, or a payment already in
  // flight): the charge may have gone through, so this view must never submit another one — sticky like
  // GuaranteeAdjustmentPage's `notice.ends`, until the guest closes or remounts this view.
  const [chargeLocked, setChargeLocked] = useState(false);
  const [amountId, hintId, contactId, lockedId] = [useId(), useId(), useId(), useId()];

  const effectiveMax = useMemo(() => {
    const allowed = restaurant?.allowedPartySizes;
    if (allowed && allowed.length > 0) return Math.max(...allowed);
    if (restaurant?.maxPartySizeAllowed) return restaurant.maxPartySizeAllowed;
    return MAX_PARTY_SIZE;
  }, [restaurant?.allowedPartySizes, restaurant?.maxPartySizeAllowed]);

  const maxSize = Math.min(effectiveMax, sizeInfo.length);
  const currentIndex = selectedSize - 1;
  const isAvailable = sizeInfo[currentIndex]?.available ?? false;
  const isSameSize = selectedSize === reservation.partySize;

  const { quote, show, requote } = usePartySizeQuote(
    ['profile', reservation.id],
    selectedSize,
    !isSameSize && isAvailable,
    (size) => fetchPartySizeQuote(reservation.id, size),
  );
  // A deposit top-up charges the card now, on the processor the quote names: the stored card on Stripe, the one entered on MercadoPago.
  const charge = chargeNow(quote);
  // The card form mints its token with this venue's MercadoPago key: a venue that serves none cannot take the card here.
  const mintsMercadoPagoTokens = restaurant?.paymentProcessor === 'mercadopago';
  const cardFormPays = (adjustment: GuaranteeAdjustment | null) => chargeNow(adjustment)?.processor === 'mercadopago' && mintsMercadoPagoTokens;
  const paysByCardForm = cardFormPays(quote);
  const cardFormUnavailable = charge?.processor === 'mercadopago' && !mintsMercadoPagoTokens;
  const cardRef = useRef<MercadoPagoCardHandle>(null);
  const guarantee: AcceptedGuarantee | undefined = quote && (quote.requires === GUARANTEE_REQUIRES.ACCEPTANCE || charge)
    ? { newFee: quote.newFee, outstanding: charge?.amount }
    : undefined;
  const requiresAcceptance = guarantee !== undefined;
  // Acceptances belong to one amount: a re-quoted fee or charge starts them over.
  const acceptanceScope = quote ? `${selectedSize}:${quote.newFee}:${quote.newFeeUsd}:${charge?.amount}:${charge?.currency}:${charge?.processor}` : '';
  const [acceptances, setAcceptances] = useState({ scope: '', accepted: NOTHING_ACCEPTED });
  const accepted = acceptances.scope === acceptanceScope ? acceptances.accepted : NOTHING_ACCEPTED;
  const acceptanceRows: [Acceptance, string][] = [
    ['honorReservation', tBooking('CONFIRM_GUARANTEE_HONOR_RESERVATION')],
    ...(cancellationHours > 0
      ? [['cancelReservation', tBooking('CONFIRM_GUARANTEE_CAN_CANCEL', { hours: cancellationHours })] as [Acceptance, string]]
      : []),
    ['termsAndConditions', tBooking('TERMS_ACCEPT_CHECKBOX')],
  ];
  const allAccepted = acceptanceRows.every(([key]) => accepted.has(key));
  const contactOnly = unacceptableSize === selectedSize || cardFormUnavailable;
  // Under the contact note there is nothing to accept.
  const showsAcceptances = requiresAcceptance && !contactOnly;
  const awaitingAcceptance = showsAcceptances && !allAccepted;
  const confirmBlocked = awaitingAcceptance || contactOnly || chargeLocked;
  // The confirm step's payment surface (the stored-card line on Stripe, the card form on MercadoPago); null while chargeLocked.
  const paymentSurface = chargeLocked ? null : paysByCardForm ? 'mercadopago' : charge?.processor === 'stripe' ? 'stripe' : null;
  const toggleAcceptance = (key: Acceptance) => {
    const next = new Set(accepted);
    if (!next.delete(key)) next.add(key);
    setAcceptances({ scope: acceptanceScope, accepted: next });
  };

  useEffect(() => {
    if (sizeInfo.length === 0) return;
    setSelectedSize((s) => Math.min(Math.max(s, minSize), maxSize));
  }, [sizeInfo.length, minSize, maxSize]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const policy = await checkModifyTimePolicy(reservation.id);
        if (cancelled) return;
        setCancellationHours(policy.cancellationPolicyHours);

        if (!policy.canModify) {
          setError(t('MODIFY_RESERVATION_STRING_12_POLICY'));
          setStep('choose');
          return;
        }

        if (!subdomainPrefix) {
          setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
          setStep('choose');
          return;
        }

        const sizes = await fetchAvailableSizesForEdition(
          subdomainPrefix,
          reservation.id,
        );
        if (cancelled) return;

        if (sizes.length === 0) {
          setError(t('MODIFY_RESERVATION_STRING_12_NO_SIZES'));
          setStep('choose');
          return;
        }

        setSizeInfo(sizes);
        setStep('choose');
      } catch {
        if (!cancelled) {
          setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
          setStep('choose');
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [reservation.id, subdomainPrefix, t]);

  const refreshReservations = async () => {
    if (restaurant?.id) {
      await queryClient.invalidateQueries({ queryKey: userReservationsKeys.byRestaurant(restaurant.id) });
    }
  };

  const handleConfirmStep = () => {
    setError(null);
    setStep('confirm');
  };

  /** The MercadoPago card for a top-up; undefined when none is needed, null when the card must be corrected. */
  const mercadoPagoCard = async (): Promise<AcceptedGuarantee['card'] | null> => {
    if (paymentSurface !== 'mercadopago') return undefined;
    try {
      return await cardRef.current?.tokenize();
    } catch (err) {
      if (err instanceof CardInputError) {
        setError(tBooking(err.message));
      } else {
        setError(tBooking('TRADITIONAL_GRAB_ERROR'));
        capturePaymentError(err, 'mercadopago', { restaurantId: restaurant?.id, reservationId: reservation.id, stage: 'party-size-top-up-token' });
      }
      return null;
    }
  };

  const handleConfirm = async () => {
    const info = sizeInfo[selectedSize - 1];
    if (!info || confirmBlocked || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const card = await mercadoPagoCard();
      if (card === null) {
        setSubmitting(false);
        return;
      }
      await submitSizeChange(reservation.id, selectedSize, info.areaId, guarantee && { ...guarantee, card });
      await refreshReservations();
      onSuccess();
    } catch (err) {
      setSubmitting(false);
      const refusal = guaranteeRefusal(err);
      // A payment the card form here can make: render the card step from its quote.
      const payable = refusal?.reason === GUARANTEE_REFUSAL.PAYMENT_REQUIRED && cardFormPays(refusal.guaranteeAdjustment)
        ? refusal.guaranteeAdjustment
        : null;
      const serverFailed = err instanceof ApiError && (err.status ?? 0) >= 500;
      // A charge whose answer never arrived may have gone through, as may one still in flight or one a server failed part-way through.
      const chargeUnanswered = guarantee?.outstanding !== undefined && err instanceof ApiError && (err.isNetworkError() || serverFailed);
      if (chargeUnanswered || refusal?.reason === GUARANTEE_REFUSAL.PAYMENT_IN_PROGRESS) {
        // A server failure is reported; a dropped connection, a timeout and a payment in flight are not.
        if (serverFailed) captureApiError(err, { source: 'change-size-submit', reservationId: reservation.id });
        // The charge may have gone through: paying again from this view could double-charge, so refuse every
        // further charging submit here on (mirrors GuaranteeAdjustmentPage's `pay()` after `refused()` sets `ends`).
        setChargeLocked(true);
        // Say so, and show the reservations as they are now.
        setError(tBooking('GUARANTEE_CHANGE_PAYMENT_IN_PROGRESS'));
        setSelectedSize(reservation.partySize);
        setStep('choose');
        void refreshReservations();
      } else if (refusal && isPaymentRefusal(refusal.reason)) {
        if (refusal.reason === GUARANTEE_REFUSAL.PAYMENT_REQUIRES_ACTION) {
          capturePaymentError(err, charge?.processor ?? 'stripe', { reservationId: reservation.id, stage: 'party-size-top-up' });
        }
        setError(tBooking('GUARANTEE_CHANGE_PAYMENT_DECLINED'));
        setSelectedSize(reservation.partySize);
        setStep('choose');
      } else if (payable) {
        setError(tBooking('GUARANTEE_CHANGE_CARD_REQUIRED'));
        show(payable);
      } else if (refusal?.reason === GUARANTEE_REFUSAL.COMPOSITION_MISMATCH) {
        setError(tBooking('GUARANTEE_CHANGE_BOOKING_CHANGED'));
        requote();
        void refreshReservations();
      } else if (refusal?.guaranteeAdjustment && refusal.reason === GUARANTEE_REFUSAL.QUOTE_CHANGED) {
        setError(tBooking('GUARANTEE_CHANGE_QUOTE_CHANGED'));
        show(refusal.guaranteeAdjustment);
      } else if (refusal?.guaranteeAdjustment && refusal.reason === GUARANTEE_REFUSAL.ACCEPTANCE_REQUIRED) {
        // The acceptances render with the hint below them.
        show(refusal.guaranteeAdjustment);
      } else if (refusal?.reason === GUARANTEE_REFUSAL.ACCEPTANCE_REQUIRED || refusal?.reason === GUARANTEE_REFUSAL.PAYMENT_REQUIRED) {
        // Nothing this app can show or pay here (a payment at a venue without the MercadoPago card form).
        captureApiError(err, { source: 'change-size-submit', reservationId: reservation.id });
        setUnacceptableSize(selectedSize);
      } else {
        // `409 false` is the no-availability answer, not a failure.
        if (!(err instanceof ApiError && err.status === 409 && err.body === false)) {
          captureApiError(err, { source: 'change-size-submit', reservationId: reservation.id });
        }
        setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
        setStep('choose');
      }
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setError(null);
      setStep('choose');
    } else {
      onClose();
    }
  };

  if (step === 'loading') {
    return (
      <div className="w-full flex flex-col items-center py-12">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-8">{t('MODIFY_RESERVATION_STRING_12_CONFIRM_TITLE')}</h2>

        <div className="flex items-center gap-4 mb-6">
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">{t('MODIFY_RESERVATION_STRING_12_CURRENT')}</span>
            <span className="text-lg font-semibold text-brand-dark">
              {t('PROFILE_PERSON', { count: reservation.partySize })}
            </span>
          </div>
          <ArrowRight size={20} className="text-foreground/40 mt-4" />
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">&nbsp;</span>
            <span className="text-lg font-semibold text-brand-dark">
              {t('PROFILE_PERSON', { count: selectedSize })}
            </span>
          </div>
        </div>

        <p className="text-sm text-foreground/70 text-center mb-8 max-w-xs">
          {t('MODIFY_RESERVATION_STRING_12_CONFIRM_MSG')}
        </p>

        <GuaranteeChangeNotice adjustment={quote} className="max-w-xs mb-4" amountId={amountId} />
        {paymentSurface === 'stripe' && (
          <p className="w-full max-w-xs text-sm text-foreground/70 text-center mb-4">{tBooking('GUARANTEE_CHANGE_STORED_CARD')}</p>
        )}
        {paymentSurface === 'mercadopago' && (
          <MercadoPagoCardFields ref={cardRef} allowAMEXBR={restaurant?.allowAMEXBR} className="max-w-xs mb-4" />
        )}
        {showsAcceptances && (
          <div role="group" aria-labelledby={amountId} className="w-full max-w-xs mb-4">
            {acceptanceRows.map(([key, label]) => (
              <div key={key} className="flex items-start gap-3 py-2">
                <Checkbox
                  id={`guarantee-${key}`}
                  checked={accepted.has(key)}
                  onCheckedChange={() => toggleAcceptance(key)}
                  className="mt-0.5"
                />
                <label htmlFor={`guarantee-${key}`} className="text-sm text-foreground/80 leading-snug cursor-pointer">
                  {label}
                </label>
              </div>
            ))}
          </div>
        )}
        {chargeLocked ? (
          // Sticky, like GuaranteeAdjustmentPage's `notice.ends`: re-derived from chargeLocked, not from `error`,
          // so stepping back into this screen after a re-pick can never lose it the way `error` does.
          <p id={lockedId} role="alert" className="w-full max-w-xs text-destructive text-sm text-center mb-4">{tBooking('GUARANTEE_CHANGE_PAYMENT_IN_PROGRESS')}</p>
        ) : error ? (
          <p role="alert" className="w-full max-w-xs text-destructive text-sm text-center mb-4">{error}</p>
        ) : null}
        {contactOnly && (
          <p id={contactId} role="alert" className="w-full max-w-xs text-destructive text-sm text-center mb-4">{tBooking('RESERVATION_STATUS_GUARANTEED_CONTACT_NOTE')}</p>
        )}

        <div className="w-full max-w-xs space-y-3">
          {awaitingAcceptance && (
            <p id={hintId} className="text-xs text-muted-foreground text-center">{tBooking('GUARANTEE_CHANGE_ACCEPT_TO_CONTINUE')}</p>
          )}
          <button
            className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={submitting}
            aria-disabled={confirmBlocked}
            aria-describedby={chargeLocked ? lockedId : awaitingAcceptance ? hintId : contactOnly ? contactId : undefined}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              t('MODIFY_RESERVATION_STRING_12_CONFIRM')
            )}
          </button>
          <button
            className="w-full py-3 rounded-full border border-border text-brand-dark font-medium text-sm tracking-wide hover:bg-foreground/5 transition-colors"
            onClick={handleBack}
            disabled={submitting}
          >
            {t('MODIFY_RESERVATION_STRING_12_BACK')}
          </button>
        </div>
      </div>
    );
  }

  // Quick-select buttons: minSize through minSize+3
  const quickSizes = Array.from({ length: 4 }, (_, i) => minSize + i).filter(
    (s) => s <= maxSize,
  );
  const showSpinner = maxSize > minSize + 3;
  // From chargeLocked, like the confirm step's lock alert: handleConfirmStep and handleBack clear `error`.
  const chooseAlert = chargeLocked ? tBooking('GUARANTEE_CHANGE_PAYMENT_IN_PROGRESS') : error;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full flex items-center mb-8">
        <button
          className="p-2 -ml-2 text-brand-dark hover:text-brand transition-colors"
          onClick={handleBack}
          aria-label={t('MODIFY_RESERVATION_STRING_12_BACK')}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-3xl font-display flex-1 text-center pr-8">
          {t('MODIFY_RESERVATION_STRING_12_TITLE')}
        </h2>
      </div>

      {chooseAlert ? (
        <p role="alert" className="text-red-500 text-sm text-center mb-6">{chooseAlert}</p>
      ) : null}

      {maxSize > 0 && (
        <>
          {/* Quick-select buttons */}
          <div className="flex gap-3 mb-6">
            {quickSizes.map((size) => {
              const isCurrent = size === reservation.partySize;
              const isSelected = size === selectedSize;

              return (
                <button
                  key={size}
                  className={cn(
                    'w-14 h-14 rounded-full text-lg font-semibold transition-colors',
                    isSelected && !isCurrent &&
                      'bg-brand-dark text-brand-foreground',
                    isCurrent && isSelected &&
                      'bg-brand-dark/10 text-brand-dark border border-brand-dark/30',
                    isCurrent && !isSelected &&
                      'bg-brand-dark/10 text-brand-dark border border-brand-dark/30',
                    !isSelected && !isCurrent &&
                      'bg-foreground/10 text-foreground/70 hover:bg-foreground/20',
                  )}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              );
            })}
          </div>

          {/* Spinner for larger sizes */}
          {showSpinner && (() => {
            const spinnerValue = selectedSize > minSize + 3 ? selectedSize : minSize + 4;
            const isSpinnerActive = selectedSize > minSize + 3;

            return (
              <div className="flex items-center gap-4 mb-6">
                <button
                  className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => setSelectedSize((s) => Math.max(minSize + 4, s - 1))}
                  disabled={!isSpinnerActive || selectedSize <= minSize + 4}
                  aria-label="-"
                >
                  <Minus size={18} />
                </button>
                <button
                  className={cn(
                    'text-2xl font-semibold w-12 text-center rounded-lg py-1 transition-colors',
                    isSpinnerActive
                      ? 'text-brand-dark'
                      : 'text-foreground/40',
                  )}
                  onClick={() => setSelectedSize(minSize + 4)}
                >
                  {spinnerValue}
                </button>
                <button
                  className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => {
                    if (!isSpinnerActive) {
                      setSelectedSize(minSize + 4);
                    } else {
                      setSelectedSize((s) => Math.min(maxSize, s + 1));
                    }
                  }}
                  disabled={isSpinnerActive && selectedSize >= maxSize}
                  aria-label="+"
                >
                  <Plus size={18} />
                </button>
              </div>
            );
          })()}

          {/* Availability status */}
          {!isSameSize && (
            <p className={cn(
              'text-sm font-medium mb-6',
              isAvailable ? 'text-green-600' : 'text-red-500',
            )}>
              {isAvailable
                ? t('MODIFY_RESERVATION_STRING_12_AVAILABLE')
                : t('MODIFY_RESERVATION_STRING_12_UNAVAILABLE')}
            </p>
          )}

          <GuaranteeChangeNotice adjustment={quote} className="max-w-xs mb-6" />

          {/* Confirm button */}
          <div className="w-full max-w-xs">
            <button
              className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50"
              onClick={handleConfirmStep}
              disabled={isSameSize || !isAvailable}
            >
              {t('MODIFY_RESERVATION_STRING_12_CONFIRM')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangeSizeView;
